import { Box, Flex, Text } from '@chakra-ui/react';
import { departmentTileColors } from '@/utils/departmentColors';
import type { ChatListItem } from '../useCommunicationPage';
import { MutedBell } from './MutedBell';
import { UnreadBadge } from './UnreadBadge';

type Props = {
  item: ChatListItem;
  color: string;
  active: boolean;
  onSelect: () => void;
};

/** One card in the department carousel: a letter tile, the name, and an unread dot. */
export function DepartmentCard({ item, color, active, onSelect }: Props) {
  const { bg, fg } = departmentTileColors(color);
  const letter = item.title.trim().charAt(0).toUpperCase() || '?';

  return (
    <Box
      role="button"
      tabIndex={0}
      position="relative"
      flexShrink={0}
      // Fixed on touch so the row reads as a scrollable carousel with a card peeking in
      // from the edge; two per line once the desktop pane wraps them.
      w={{ base: '124px', lg: 'calc(50% - 6px)' }}
      p={3}
      borderRadius="xl"
      borderWidth={1}
      borderColor={active ? 'volyx.action' : 'volyx.borderLight'}
      bg="volyx.bg"
      boxShadow="0 1px 2px rgba(0, 0, 0, 0.04)"
      cursor="pointer"
      scrollSnapAlign="start"
      _hover={{ borderColor: active ? 'volyx.action' : 'volyx.mediumGray' }}
      _active={{ transform: 'scale(0.98)' }}
      transition="border-color 0.12s ease-out, transform 0.12s ease-out"
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {item.muted ? <MutedBell position="corner" /> : null}
      <UnreadBadge count={item.unreadCount} muted={item.muted} position="corner" />
      <Flex
        w="40px"
        h="40px"
        borderRadius="lg"
        align="center"
        justify="center"
        bg={bg}
        color={fg}
        fontWeight="bold"
        fontSize="lg"
        mb={3}
      >
        {letter}
      </Flex>
      <Text
        fontSize="sm"
        fontWeight={item.unreadCount > 0 ? 'bold' : 'semibold'}
        color="gray.800"
        lineClamp={2}
      >
        {item.title}
      </Text>
    </Box>
  );
}
